// /profile - the signed-in user's page: identity from CurrentUserContext
// (no extra fetch), the authenticator-app ceremony, notification
// subscriptions over GET/PUT /api/notifications/settings (optimistic,
// auto-saved per change), and the email delivery history the bell
// already fetches.

import { initialsOf } from "../api/auth";
import { fmtDate } from "../lib/dates";
import {
  useNotifications,
  useNotificationSettings,
  useUpdateNotificationSettings,
} from "../api/notifications";
import { useCurrentUser } from "../components/auth/CurrentUserContext";
import { isAdminRole } from "../lib/roles";
import { Skeleton, SkeletonGroup } from "../components/chrome/Skeleton";
import { SecuritySection } from "../components/profile/SecuritySection";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const TIMEZONES = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];

const ALERT_THRESHOLDS = [12, 24, 48, 72, 168];

export function Profile() {
  const user = useCurrentUser();
  // The digest and the deadline alerts go to admin+ only (the server picks
  // the recipients); a control for anyone else would be a dead switch.
  const receivesFirmMail = isAdminRole(user?.role);
  const settingsQuery = useNotificationSettings(receivesFirmMail);
  const update = useUpdateNotificationSettings();
  const history = useNotifications({ refetchInterval: false });
  // A form notice carries no delivered fact: it is the bell's, not a delivery.
  const deliveries = history.items.filter((item) => item.delivered !== null);
  // A failed poll keeps the rows the last one read; the line stands only when it kept none.
  const failedEmpty = history.isError && history.items.length === 0;

  const settings = settingsQuery.data;
  const timezones = settings?.digestTimezone
    ? Array.from(new Set([...TIMEZONES, settings.digestTimezone]))
    : TIMEZONES;
  const thresholds = settings?.alertThresholdHours
    ? Array.from(new Set([...ALERT_THRESHOLDS, settings.alertThresholdHours])).sort((a, b) => a - b)
    : ALERT_THRESHOLDS;

  return (
    <main className="cc-main">
      <div className="cc-body profile-body">
        <section className="cc-card profile-identity">
          {user ? (
            <>
              <span className="cc-user-av profile-av" aria-hidden>
                {initialsOf(user.fullName)}
              </span>
              <div className="profile-identity-text">
                <h2>{user.fullName}</h2>
                <div className="profile-identity-mail">{user.email}</div>
                <div className="profile-identity-meta">
                  <span className="profile-chip">{user.role}</span>
                  <span className="profile-chip">
                    {user.signupSource === "google" ? "Signed in with Google" : "Password account"}
                  </span>
                  {user.createdAt && (
                    <span className="profile-chip">
                      member since {fmtDate(user.createdAt, "never")}
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <SkeletonGroup>
              <Skeleton width={220} height={20} />
              <Skeleton width={160} />
            </SkeletonGroup>
          )}
        </section>

        <SecuritySection />

        <section className="cc-card profile-section">
          <h3>Email notifications</h3>

          {user && !receivesFirmMail && (
            <div className="profile-row-sub">
              The weekly digest and deadline alerts go to the firm&apos;s admins.
            </div>
          )}
          {settingsQuery.isLoading && (
            <SkeletonGroup>
              <Skeleton />
              <Skeleton />
              <Skeleton width={180} />
            </SkeletonGroup>
          )}
          {settingsQuery.isError && (
            <div className="profile-error">Could not load notification settings.</div>
          )}

          {settings && (
            <>
              <label className="profile-row">
                <input
                  type="checkbox"
                  checked={settings.digestEnabled}
                  onChange={(e) => update.mutate({ digestEnabled: e.target.checked })}
                />
                <span>
                  <span className="profile-row-title">Weekly digest</span>
                  <span className="profile-row-sub">
                    Deadlines, hearings and conflicts for the coming weeks
                  </span>
                </span>
              </label>

              <div className="profile-controls" data-disabled={!settings.digestEnabled}>
                <label>
                  Day
                  <select
                    value={settings.digestDay}
                    disabled={!settings.digestEnabled}
                    onChange={(e) => update.mutate({ digestDay: Number(e.target.value) })}
                  >
                    {DAY_NAMES.map((name, i) => (
                      <option key={name} value={i}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Hour
                  <select
                    value={settings.digestHour}
                    disabled={!settings.digestEnabled}
                    onChange={(e) => update.mutate({ digestHour: Number(e.target.value) })}
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <option key={h} value={h}>
                        {String(h).padStart(2, "0")}:00
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Timezone
                  <select
                    value={settings.digestTimezone}
                    disabled={!settings.digestEnabled}
                    onChange={(e) => update.mutate({ digestTimezone: e.target.value })}
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="profile-row">
                <input
                  type="checkbox"
                  checked={settings.criticalAlertsEnabled}
                  onChange={(e) => update.mutate({ criticalAlertsEnabled: e.target.checked })}
                />
                <span>
                  <span className="profile-row-title">Critical deadline alerts</span>
                  <span className="profile-row-sub">
                    Email when a deadline enters the alert window
                  </span>
                </span>
              </label>

              <div className="profile-controls" data-disabled={!settings.criticalAlertsEnabled}>
                <label>
                  Alert window
                  <select
                    value={settings.alertThresholdHours}
                    disabled={!settings.criticalAlertsEnabled}
                    onChange={(e) => update.mutate({ alertThresholdHours: Number(e.target.value) })}
                  >
                    {thresholds.map((h) => (
                      <option key={h} value={h}>
                        {h} hours
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="profile-counters">
                <span>
                  digests this month: <span className="count">{settings.digestsSentThisMonth}</span>{" "}
                  (last {fmtDate(settings.lastDigestSentAt, "never")})
                </span>
                <span>
                  alerts this month: <span className="count">{settings.alertsSentThisMonth}</span>{" "}
                  (last {fmtDate(settings.lastAlertSentAt, "never")})
                </span>
              </div>
            </>
          )}
        </section>

        <section className="cc-card profile-section">
          <h3>Recent deliveries</h3>
          {history.isLoading && (
            <SkeletonGroup>
              <Skeleton />
              <Skeleton />
            </SkeletonGroup>
          )}
          {failedEmpty && <div className="profile-error">Could not load the deliveries.</div>}
          {!history.isLoading && !failedEmpty && deliveries.length === 0 && (
            <div className="profile-row-sub">Nothing sent yet.</div>
          )}
          <ul className="profile-history">
            {deliveries.map((item) => (
              <li key={item.id}>
                <span className={`profile-chip profile-chip-${item.alertType}`}>
                  {item.alertType}
                </span>
                <span className="profile-history-subject">{item.subject}</span>
                <span className="profile-history-date">
                  {fmtDate(item.sentAt, "never")}
                  {item.delivered === false && " (failed)"}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
}
